import { ReactSVG } from "react-svg"
import cn from 'classnames'

import IconLocale from '../../icons/icon-i18n.svg'
import IconCheck from '../../icons/icon-check.svg'
import IconChevron from '../../icons/chevron-right.svg'

import styles from './styles.module.scss'
import useLocaleSwitch from "utils/use-locale-switch"
import useViewport from "libs/use-viewport"
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import Popup from "components/ui/popup"
import { useRouter } from "next/router"
import { useTranslation } from "utils/use-translation"

const LOCALES = { 
    'de': 'Deutsch', 
    'en': 'English', 
    'es': 'Español',
    'fr': 'Français',
    'it': 'Italiana',
    'pt': 'Português',
}

interface Props {
    className?: string
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

const LocaleButton: React.FC<Props> = ({ isOpen, setIsOpen, className }) => {
    const { t } = useTranslation()
    const router = useRouter()
    const [view] = useViewport()

    const [isAccountPage, setIsAccountPage] = useState<boolean>(true)
    const [isDarkTheme, setIsDarkTheme] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const { switchLocale, currentLocale } = useLocaleSwitch()

    const isDesktop = ['desktop'].includes(view)

    const renderLocaleList = () => (Object.entries(LOCALES).map(([key, value], id) => (
        <li key={id} onClick={() => switchLocale(key)}>
            <span>{value}</span>
            {key === currentLocale && <ReactSVG src={IconCheck} className={styles.checkIcon} />}
        </li>
    )))

    const handleClick = () => {
        if (!isDesktop) {
            setIsOpen(true)
        }
    }

    const handleMouseEnter = () => {
        setIsHovered(true)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsHovered(false)
        }, 100)
    }

    useEffect(() => {
        setIsAccountPage(router.pathname.includes('account'))
        setIsDarkTheme(router.pathname.includes('private-clients'))
    }, [router])

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return (
        <>
            <button
                onClick={handleClick}
                className={cn(styles.i18n,
                    isAccountPage && styles.account,
                    !isAccountPage && styles.main,
                    isDarkTheme && styles.dark,
                    className)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <ReactSVG src={IconLocale} className={styles.localeIcon} />
                {!isDesktop && !isAccountPage && <span>{LOCALES[currentLocale]}</span>}
                {!isDesktop && !isAccountPage && <ReactSVG src={IconChevron} className={styles.chevronIcon} />}
                <ul className={cn(styles.i18nGroup, {
                    [styles.expanded]: isHovered
                })}
                >
                    {renderLocaleList()}
                </ul>
            </button>

            {isOpen && isAccountPage &&
                <Popup
                    className={styles.i18nPopup}
                    onClose={() => setIsOpen(false)}
                    heading={t('choose_language')}>
                    {renderLocaleList()}
                </Popup>
            }

            {!isAccountPage &&
                <div className={cn(styles.i18nPopupMain, isOpen && styles.open, {
                    [styles.dark__bg]: isDarkTheme,
                    [styles.dark]: isDarkTheme,
                })}>
                    <button
                        className={styles.i18nPopupMainHeader}
                        onClick={() => setIsOpen(false)}>
                        <ReactSVG src={IconChevron} className={styles.chevronIcon} />
                        <ReactSVG src={IconLocale} className={styles.localeIcon} />
                        <p>{t('choose_language')}</p>
                    </button>
                    {renderLocaleList()}
                </div>
            }
        </>
    )
}

export default LocaleButton